import React, { useEffect, useState } from 'react'
import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material'
import { Error, LocalShipping } from '@mui/icons-material'
import { Translate } from 'react-redux-i18n'

import api from 'api'
import { Itinerary } from 'types'
import APIStatus from 'types/Status'

import { CheckCircle } from './styled'

const allCompleted = (trackable: TrackableItinerary[]) =>
  trackable.length > 0 && trackable.every((t) => t.status === APIStatus.Succeeded)

const isAnyLoading = (trackable: TrackableItinerary[]) =>
  trackable.some((t) => t.status === APIStatus.Loading)

const isNotCompleted = (trackable: TrackableItinerary) => trackable.status !== APIStatus.Succeeded

type TrackableItinerary = {
  itinerary: Itinerary
  status: APIStatus
}

export type CancelItineraryDialogProps = {
  onClose: (() => void) | ((wasActionPerformed: boolean) => void)
  itineraries: Itinerary[]
}

const CancelItineraryDialog = ({ itineraries, onClose }: CancelItineraryDialogProps) => {
  const [trackable, setTrackable] = useState<TrackableItinerary[]>([])
  const [isRetrying, setIsRetrying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isDone, setIsDone] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [userDidInteract, setUserDidInteract] = useState(false)
  const multiple = trackable.length > 1

  useEffect(() => {
    const items: TrackableItinerary[] = itineraries.map((i) => ({
      itinerary: i,
      status: APIStatus.Idle,
    }))

    setTrackable(items)
  }, [itineraries])

  useEffect(() => {
    setIsLoading(isAnyLoading(trackable))
    setIsDone(allCompleted(trackable))
  }, [trackable])

  useEffect(() => {
    if (isDone) {
      setIsClosing(true)
      setTimeout(() => {
        onClose(true)
      }, 1500)
    }
  }, [isDone, onClose])

  const handleClose = () => {
    if (userDidInteract) {
      onClose(true)
    } else {
      onClose(false)
    }
  }

  const cancelItineraries = () => {
    setUserDidInteract(true)
    setTrackable((prevState) =>
      prevState.map((t) => {
        if (!isNotCompleted(t)) return t

        return { ...t, status: APIStatus.Loading }
      })
    )

    trackable.forEach((t, index) => {
      if (!isNotCompleted(t)) return

      api.Itinerary.cancel({
        itineraryId: t.itinerary.id,
      })
        .then(() => {
          setTrackable((prevState) => {
            return Object.assign([], prevState, {
              [index]: {
                itinerary: prevState[index].itinerary,
                status: APIStatus.Succeeded,
              },
            })
          })
        })
        .catch(() => {
          setIsRetrying(true)
          setTrackable((prevState) => {
            return Object.assign([], prevState, {
              [index]: {
                itinerary: prevState[index].itinerary,
                status: APIStatus.Failed,
              },
            })
          })
        })
    })
  }

  const statusIcon = (status: APIStatus) => {
    switch (status) {
      case APIStatus.Loading:
        return <CircularProgress />
      case APIStatus.Succeeded:
        return <CheckCircle color="action" />
      case APIStatus.Failed:
        return <Error color="secondary" />
      default:
        return null
    }
  }

  const getContentText = () => {
    const basePath = `UI.Itineraries.StatusMessages.Cancel.${multiple ? 'Multiple' : 'Single'}`
    const remainingAmount = trackable.filter(isNotCompleted).length
    const id = trackable[0]?.itinerary?.id

    if (isDone) {
      return <Translate value={`${basePath}.Success`} amount={trackable.length} id={id} />
    }

    if (isRetrying) {
      return (
        <>
          <Translate value={`${basePath}.Failed`} amount={remainingAmount} id={id} />
          {'. '}
          <Translate value="General.Questions.Retry" />
        </>
      )
    }

    return <Translate value={`${basePath}.Prompt`} amount={remainingAmount} id={id} />
  }

  return (
    <Dialog
      open
      onClose={() => handleClose()}
      aria-labelledby="assign-user-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="assign-user-dialog-title">
        <Translate value={`UI.Itineraries.Table.Cancel.${multiple ? 'Multiple' : 'Single'}`} />
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText>{getContentText()}</DialogContentText>
        <List>
          {trackable.map((t) => (
            <ListItem key={t.itinerary.id}>
              <ListItemAvatar>
                <Avatar>
                  <LocalShipping />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={t.itinerary.id} />
              {statusIcon(t.status)}
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button disabled={isClosing || isLoading} onClick={() => cancelItineraries()} color="error">
          {isRetrying ? <Translate value="General.Retry" /> : <Translate value="General.Yes" />}
        </Button>
        <Button disabled={isClosing || isLoading} onClick={() => onClose(false)} color="primary">
          <Translate value="General.No" />
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CancelItineraryDialog
